import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
	OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { DxButtonModule } from 'devextreme-angular/ui/button';
import { DxDataGridModule } from 'devextreme-angular/ui/data-grid';
import { DxSelectBoxModule } from 'devextreme-angular/ui/select-box';
import { DxTextBoxModule } from 'devextreme-angular/ui/text-box';
import { DxTreeViewModule } from 'devextreme-angular/ui/tree-view';
import {
	FieldOption,
	MappingEntitySchema,
	MappingGridRow,
	MappingNewlineMode,
	MappingRuleMode,
	MappingRuleNormalization,
	MappingPicklistOption,
	TreeNode
} from '../../models/link-generic-mapper.models';
import { DxDropDownBoxModule } from 'devextreme-angular';

@Component({
  selector: 'app-mapping-rules-grid',
  standalone: true,
  imports: [
    CommonModule,
    DxDataGridModule,
    DxSelectBoxModule,
    DxTextBoxModule,
    DxButtonModule,
    DxTreeViewModule,
    DxDropDownBoxModule
  ],
  templateUrl: './mapping-rules-grid.component.html',
  styleUrl: './mapping-rules-grid.component.scss'
})
export class MappingRulesGridComponent implements OnChanges, OnDestroy {
	@ViewChild(DxDataGridComponent) dataGrid?: DxDataGridComponent;

  @Input({ required: true }) schema!: MappingEntitySchema;
  @Input({ required: true }) mappingRows: MappingGridRow[] = [];
  @Input({ required: true }) sourceFieldOptions: FieldOption[] = [];
  @Input() sourceSystemName = 'Source';
  @Input() targetSystemName = 'Target';
  @Input() referenceFieldOptionsByEntity: Record<string, FieldOption[]> = {};
  @Input() getRowState: (
    row: MappingGridRow
  ) => 'issue' | 'ready' | 'ignored' | 'readonly' | 'pending' | 'loading' = () => 'pending';
  @Input() getModeDescription: (row: MappingGridRow) => string = () => '';
  @Input() selectedRowId: string | null = null;
  @Input() sourceTreeItems: TreeNode[] = [];
	@Input() layoutStorageKey = '';

  @Output() mappingModeChanged = new EventEmitter<MappingGridRow>();
  @Output() previewRequested = new EventEmitter<MappingGridRow | null>();
  @Output() expressionEditRequested = new EventEmitter<MappingGridRow>();
  @Output() lookupEditRequested = new EventEmitter<MappingGridRow>();
  @Output() picklistMapRequested = new EventEmitter<MappingGridRow>();
  @Output() linkKeyChanged = new EventEmitter<MappingGridRow>();
	@Output() rowSelected = new EventEmitter<MappingGridRow>();
	@Output() childMappingRequested = new EventEmitter<MappingGridRow>();
	@Output() deferredSourceNodeExpanded = new EventEmitter<TreeNode>();

	readonly gridPaging = { enabled: false };
	readonly gridSorting = { mode: 'none' };
	readonly sourceDropDownOptions = { width: 420, maxWidth: 'calc(100vw - 24px)' };
	readonly constantPicklistDropDownOptions = { hideOnOutsideClick: true };
	readonly booleanConstantOptions: MappingPicklistOption[] = [
		{ value: 'true', label: 'True' },
		{ value: 'false', label: 'False' }
	];
	readonly resizableColumnNames = ['linkKey', 'field', 'mode', 'mapping', 'preview', 'status'];

	private pendingScrollAnchor: { rowId: string; rowTop: number } | null = null;
	private pendingScrollAnchorTimeout: ReturnType<typeof setTimeout> | null = null;
	private sourceDropdownPrewarmTimeoutByRowId: Record<string, ReturnType<typeof setTimeout>> = {};
	private appliedLayoutStorageKey = '';
	private applyingColumnLayout = false;

  sourceFieldSearchByRowId: Record<string, string> = {};
  sourceTreeItemsByRowId: Record<string, TreeNode[]> = {};
  sourceFieldOptionsByRowId: Record<string, FieldOption[]> = {};
	constantPicklistOptionsByRowId: Record<string, MappingPicklistOption[]> = {};

	sourceDropdownOpenedByRowId: Record<string, boolean> = {};
	normalizationExpandedByRowId: Record<string, boolean> = {};
	normalizationNewlineModes: Array<{ value: MappingNewlineMode; label: string }> = [
		{ value: 'Preserve', label: 'Keep' },
		{ value: 'LF', label: 'LF' },
		{ value: 'CRLF', label: 'CRLF' },
		{ value: 'Spaces', label: 'Spaces' },
		{ value: 'Remove', label: 'Remove' }
	];

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['mappingRows'] ||
      changes['sourceTreeItems'] ||
      changes['sourceFieldOptions']
    ) {
      this.resetRowCaches();
    }

		if (changes['mappingRows']) {
			this.scheduleScrollAnchorRestore('mappingRows.changed');
		}

		if (changes['layoutStorageKey']) {
			this.appliedLayoutStorageKey = '';
			setTimeout(() => this.applyStoredColumnLayout(), 0);
		}
  }

	ngOnDestroy(): void {
		this.clearSourceDropdownPrewarmTimeouts();
		this.clearPendingScrollAnchor();
	}

  isSourceDropdownOpened(row: MappingGridRow): boolean {
  return !!this.sourceDropdownOpenedByRowId[row.targetFieldId];
}

setSourceDropdownOpened(row: MappingGridRow, value: boolean): void {
  this.sourceDropdownOpenedByRowId[row.targetFieldId] = value;
}

	getSourceDropdownDisplayValue(row: MappingGridRow): string {
	  const selected = this.getSelectedSourceField(row);
	  return selected?.label || row.sourceFieldPath || '';
	}

	getModeSelectElementAttr(row: MappingGridRow): Record<string, string> {
		return { 'data-cy': `mapper-row-mode-${row.targetFieldId}` };
	}

	getModeEditorElementAttr(row: MappingGridRow): Record<string, string> {
		return { 'data-cy': `mapper-mode-editor-${row.targetFieldId}` };
	}

	getSourceDropDownElementAttr(row: MappingGridRow): Record<string, string> {
		return { 'data-cy': `mapper-row-source-${row.targetFieldId}` };
	}

	getConstantValueElementAttr(row: MappingGridRow): Record<string, string> {
		return { 'data-cy': `mapper-constant-value-${row.targetFieldId}` };
	}

	getPicklistMapButtonElementAttr(row: MappingGridRow): Record<string, string> {
		return { class: 'picklist-map-btn', 'data-cy': `mapper-picklist-map-${row.targetFieldId}` };
	}

	getChildMapButtonElementAttr(row: MappingGridRow): Record<string, string> {
		return { 'data-cy': `mapper-map-child-${row.targetFieldId}` };
	}

	getExpressionEditButtonElementAttr(row: MappingGridRow): Record<string, string> {
		return { class: 'expression-edit-btn', 'data-cy': `mapper-expression-edit-${row.targetFieldId}` };
	}

	getLookupEditButtonElementAttr(row: MappingGridRow): Record<string, string> {
		return { class: 'lookup-edit-btn', 'data-cy': `mapper-lookup-edit-${row.targetFieldId}` };
	}

	closeSourceDropdown(row: MappingGridRow): void {
  this.sourceDropdownOpenedByRowId[row.targetFieldId] = false;
}

  selectRow(row: MappingGridRow | null | undefined): void {
    if (!row) {
      return;
    }

    if (this.selectedRowId === row.targetFieldId) {
      return;
    }

    this.selectedRowId = row.targetFieldId;
    this.rowSelected.emit(row);
  }

  onRowClick(e: any): void {
    this.selectRow(e?.data);
  }

  onFocusedRowChanged(e: any): void {
    this.selectRow(e?.row?.data);
  }

	onGridContentReady(): void {
		this.applyStoredColumnLayout();
		this.restorePendingScrollAnchor('grid.contentReady');
	}

	onGridOptionChanged(e: any): void {
		if (this.applyingColumnLayout || !this.layoutStorageKey || e?.name !== 'columns') {
			return;
		}

		if (!/^columns\[\d+\]\.width$/.test(e.fullName ?? '')) {
			return;
		}

		this.saveColumnLayout(e.component);
	}

	private applyStoredColumnLayout(): void {
		if (!this.layoutStorageKey || this.appliedLayoutStorageKey === this.layoutStorageKey || !this.dataGrid?.instance) {
			return;
		}

		const widths = this.readStoredColumnWidths();
		this.applyingColumnLayout = true;
		try {
			for (const columnName of this.resizableColumnNames) {
				const width = widths[columnName];
				if (this.isValidColumnWidth(width)) {
					this.dataGrid.instance.columnOption(columnName, 'width', width);
				}
			}

			this.appliedLayoutStorageKey = this.layoutStorageKey;
		} finally {
			this.applyingColumnLayout = false;
		}
	}

	private saveColumnLayout(gridInstance: any): void {
		if (!gridInstance || !this.canUseLocalStorage()) {
			return;
		}

		const widths: Record<string, number> = {};
		for (const columnName of this.resizableColumnNames) {
			const width = gridInstance.columnOption(columnName, 'width');
			if (this.isValidColumnWidth(width)) {
				widths[columnName] = width;
			}
		}

		localStorage.setItem(this.layoutStorageKey, JSON.stringify(widths));
	}

	private readStoredColumnWidths(): Record<string, number> {
		if (!this.canUseLocalStorage()) {
			return {};
		}

		try {
			const raw = localStorage.getItem(this.layoutStorageKey);
			const parsed = raw ? JSON.parse(raw) : {};
			return parsed && typeof parsed === 'object'
				? parsed
				: {};
		} catch {
			return {};
		}
	}

	private isValidColumnWidth(width: unknown): width is number {
		return typeof width === 'number' && Number.isFinite(width) && width >= 40;
	}

	private canUseLocalStorage(): boolean {
		return typeof localStorage !== 'undefined';
	}

  canUseAsLinkKey(row: MappingGridRow): boolean {
		if (row.isLinkKey) {
			return true;
		}

		if (!this.canEditMapping(row) || (row.mode !== 'SourceField' && row.mode !== 'Expression')) {
			return false;
		}

		return row.mode === 'SourceField'
			? !!row.sourceFieldPath
			: !!row.value?.trim();
  }

  getLinkKeyTooltip(row: MappingGridRow): string {
		if (row.isLinkKey) {
			if (row.matchOnlyLinkKey) {
				return 'Non-standard match-only key: used to find the target record, but never written to the target';
			}

			return 'Shared record link key for create and update';
		}

    if (this.canUseAsLinkKey(row)) {
			return 'Use this mapped field as the shared record link key for create and update';
    }

	if (row.readOnly && !row.matchOnlyLinkKey) {
	  return 'This read-only field cannot be queried as the record link key';
	}

		if (row.mode !== 'SourceField' && row.mode !== 'Expression') {
			return 'Only source-field and expression mappings can be used as the record link key';
		}

		if (row.mode === 'Expression') {
			return 'Enter an expression before using this row as the record link key';
		}

		return 'Select a source field before using this row as the record link key';
  }

  onLinkKeyChanged(row: MappingGridRow, event: Event): void {
		this.clearPendingScrollAnchor();
    const target = event?.target as HTMLInputElement | null;
    row.isLinkKey = !!target?.checked && this.canUseAsLinkKey(row);
    this.selectRow(row);
    this.linkKeyChanged.emit(row);
  }

	canEditMapping(row: MappingGridRow): boolean {
		return !row.readOnly || row.matchOnlyLinkKey === true;
	}

  onCellPrepared(e: any): void {
    if (e.rowType !== 'data' || !e.data) {
      return;
    }

    const state = this.getRowState(e.data);
    const rowElement = e.rowElement as HTMLElement | undefined;
    if (rowElement?.classList) {
      rowElement.classList.remove(
        'row-state-ready',
        'row-state-pending',
        'row-state-issue',
        'row-state-ignored',
        'row-state-readonly',
        'row-state-loading',
        'row-is-selected'
      );
      rowElement.classList.add(`row-state-${state}`);

      if (e.data.targetFieldId === this.selectedRowId) {
        rowElement.classList.add('row-is-selected');
      }
    }

    if (e.data.targetFieldId === this.selectedRowId) {
      e.cellElement?.classList?.add('grid-selected-cell');
    }
  }

  canOpenPicklistMap(row: MappingGridRow): boolean {
    if (!row.isPicklist || row.mode !== 'SourceField' || !row.sourceFieldPath) {
      return false;
    }

    const source = this.getSelectedSourceField(row);
    return !!source?.isPicklist;
  }

  isChildMappingRow(row: MappingGridRow): boolean {
    return !!row && (!!row.isCollection || !!row.isChildObject);
  }

  getMappedPicklistCount(row: MappingGridRow): number {
    return row.picklistMapping?.items?.filter(x => !!x.targetValue).length ?? 0;
  }

  getSourceFieldOptionsForRow(row: MappingGridRow): FieldOption[] {
    const cached = this.sourceFieldOptionsByRowId[row.targetFieldId];
    if (cached) {
      return cached;
    }

    const computed = row.sourceFieldOptions?.length
      ? [...row.sourceFieldOptions]
      : [...this.sourceFieldOptions]
          .filter(option => this.isSourceFieldCompatible(row, option))
          .sort(
            (a, b) =>
              this.getSourceFieldScore(row, a) - this.getSourceFieldScore(row, b) ||
              a.label.localeCompare(b.label)
          );

    this.sourceFieldOptionsByRowId[row.targetFieldId] = computed;
    return computed;
  }

  getSelectedSourceField(row: MappingGridRow): FieldOption | null {
    if (!row.sourceFieldPath) {
      return null;
    }

    return this.getSourceFieldOptionsForRow(row).find(x => x.path === row.sourceFieldPath) ?? null;
  }

	prewarmSourceDropdown(row: MappingGridRow): void {
		if (this.isSourceDropdownPrepared(row) || this.sourceDropdownPrewarmTimeoutByRowId[row.targetFieldId]) {
			return;
		}

		this.sourceDropdownPrewarmTimeoutByRowId[row.targetFieldId] = setTimeout(() => {
			delete this.sourceDropdownPrewarmTimeoutByRowId[row.targetFieldId];
			this.prepareSourceDropdown(row);
		}, 0);
	}

	prepareSourceDropdown(row: MappingGridRow): void {
		const pendingTimeout = this.sourceDropdownPrewarmTimeoutByRowId[row.targetFieldId];
		if (pendingTimeout) {
			clearTimeout(pendingTimeout);
			delete this.sourceDropdownPrewarmTimeoutByRowId[row.targetFieldId];
		}

		this.getSourceFieldOptionsForRow(row);
		this.getCachedSourceTreeItems(row);
	}

	isSourceDropdownPrepared(row: MappingGridRow): boolean {
		return Object.prototype.hasOwnProperty.call(this.sourceFieldOptionsByRowId, row.targetFieldId)
			&& Object.prototype.hasOwnProperty.call(this.sourceTreeItemsByRowId, row.targetFieldId);
	}

	onSourceDropdownPointerDown(event: Event, row: MappingGridRow): void {
		event.stopPropagation();
		this.prepareSourceDropdown(row);
	}

  getConstantPicklistOptions(row: MappingGridRow): MappingPicklistOption[] {
		const cached = this.constantPicklistOptionsByRowId[row.targetFieldId];
		if (cached) {
			return cached;
		}

		const options = (row.targetPicklistOptions ?? []).filter(x => x.isActive !== false);
		this.constantPicklistOptionsByRowId[row.targetFieldId] = options;
		return options;
  }

	isBooleanConstant(row: MappingGridRow): boolean {
		return !row.isPicklist && row.targetDataType?.toLowerCase() === 'boolean';
	}

onModeValueChanged(row: MappingGridRow): void {
	this.captureScrollAnchor(row);
	this.mappingModeChanged.emit(row);
	this.scheduleScrollAnchorRestore('mode.valueChanged');
}

selectModeFromEvent(event: Event, row: MappingGridRow): void {
	const detail = (event as CustomEvent<{
		mode?: MappingRuleMode;
		sourceFieldPath?: string | null;
		value?: string | null;
		openExpressionEditor?: boolean;
		openLookupEditor?: boolean;
		openPicklistMappingEditor?: boolean;
	}>).detail;
	const mode = detail?.mode;
	if (!mode || !this.canEditMapping(row) || !row.allowedModes?.includes(mode)) {
		return;
	}

	event.stopPropagation();

	if (row.mode !== mode) {
		row.mode = mode;
		this.onModeValueChanged(row);
	}

	if (mode === 'SourceField' && detail.sourceFieldPath?.trim()) {
		this.applySourceFieldPathFromValue(row, detail.sourceFieldPath, 'sourceField.customEvent', true);
		if (detail.openPicklistMappingEditor) {
			this.picklistMapRequested.emit(row);
		}
		return;
	}

	if (mode === 'SourceField' && detail.openPicklistMappingEditor) {
		this.picklistMapRequested.emit(row);
		return;
	}

	if (mode === 'Constant' && 'value' in detail) {
		row.value = detail.value ?? null;
		this.previewRequested.emit(row);
		return;
	}

	if (mode === 'Expression' && detail.openExpressionEditor) {
		this.expressionEditRequested.emit(row);
		return;
	}

	if (mode === 'Lookup' && detail.openLookupEditor) {
		this.lookupEditRequested.emit(row);
	}
}

onSourceFieldValueChanged(e: any, row: MappingGridRow): void {
	const nextValue = e?.value ?? null;
	if ((row.sourceFieldPath ?? null) === nextValue) {
		return;
	}

	this.applySourceFieldPath(row, nextValue, 'sourceField.valueChanged', false);
}

selectSourceFieldFromEvent(event: Event, row: MappingGridRow): void {
	const sourceFieldPath = (event as CustomEvent<{ path?: string }>).detail?.path?.trim();
	if (!sourceFieldPath || !this.canEditMapping(row)) {
		return;
	}

	event.stopPropagation();
	this.applySourceFieldPathFromValue(row, sourceFieldPath, 'sourceField.customEvent', true);
}

onSourceDropdownClosed(): void {
	this.scheduleScrollAnchorRestore('sourceField.closed');
}

onConstantValueChanged(e: any, row: MappingGridRow): void {
  row.value = e?.value ?? null;
  this.previewRequested.emit(row);
}

setConstantValueFromEvent(event: Event, row: MappingGridRow): void {
	if (row.mode !== 'Constant' || !this.canEditMapping(row)) {
		return;
	}

	event.stopPropagation();
	row.value = (event as CustomEvent<{ value?: string | null }>).detail?.value ?? null;
	this.previewRequested.emit(row);
}



clearSourceField(row: MappingGridRow): void {
  this.captureScrollAnchor(row);
  row.sourceFieldPath = null;
  this.rebuildSourceTreeForRow(row);
  this.closeSourceDropdown(row);
  this.previewRequested.emit(row);
	this.scheduleScrollAnchorRestore('sourceField.clear');
}

	canConfigureNormalization(row: MappingGridRow): boolean {
		return !!row && !this.isChildMappingRow(row) && !row.isPicklist && row.mode !== 'Ignore';
	}

	getNormalizationSummary(row: MappingGridRow): string {
		const count = this.getActiveNormalizationCount(row);
		return count > 0
			? `${count} normalization${count === 1 ? '' : 's'}`
			: 'Normalize';
	}

	isNormalizationExpanded(row: MappingGridRow): boolean {
		return this.normalizationExpandedByRowId[row.targetFieldId] === true;
	}

	onNormalizationToggle(row: MappingGridRow, event: Event): void {
		const details = event?.target as HTMLDetailsElement | null;
		this.normalizationExpandedByRowId[row.targetFieldId] = !!details?.open;
	}

	isNormalizationFlagEnabled(row: MappingGridRow, flag: 'trimWhitespace' | 'emptyStringToNull' | 'truncateToMaxLength' | 'padLeft'): boolean {
		return row.normalization?.[flag] === true;
	}

	isNormalizationMaxLengthEnabled(row: MappingGridRow): boolean {
		return this.isNormalizationFlagEnabled(row, 'truncateToMaxLength');
	}

	getNormalizationMaxLength(row: MappingGridRow): number | null {
		return row.normalization?.maxLength ?? null;
	}

	getNormalizationTruncateSuffix(row: MappingGridRow): string {
		return row.normalization?.truncateSuffix || '';
	}

	isNormalizationPadLeftEnabled(row: MappingGridRow): boolean {
		return this.isNormalizationFlagEnabled(row, 'padLeft');
	}

	getNormalizationPadLeftToLength(row: MappingGridRow): number | null {
		return row.normalization?.padLeftToLength ?? null;
	}

	getNormalizationPadLeftChar(row: MappingGridRow): string {
		return row.normalization?.padLeftChar || ' ';
	}

	getNormalizationNewlineMode(row: MappingGridRow): string {
		return row.normalization?.newlineMode || 'Preserve';
	}

	getNormalizationNumberFormat(row: MappingGridRow): string {
		return row.normalization?.numberFormat || '';
	}

	getNormalizationDateFormat(row: MappingGridRow): string {
		return row.normalization?.dateFormat || '';
	}

	setNormalizationFlag(row: MappingGridRow, flag: 'trimWhitespace' | 'emptyStringToNull' | 'truncateToMaxLength' | 'padLeft', checked: boolean): void {
		const normalization = this.ensureNormalization(row);
		normalization[flag] = checked ? true : null;

		if (flag === 'truncateToMaxLength' && checked && !normalization.maxLength && row.maxLength) {
			normalization.maxLength = row.maxLength;
		}

		if (flag === 'truncateToMaxLength' && !checked) {
			normalization.maxLength = null;
			normalization.truncateSuffix = null;
		}

		if (flag === 'padLeft' && checked) {
			normalization.padLeftChar ||= ' ';
			normalization.padLeftToLength ??= row.maxLength ?? null;
		}

		if (flag === 'padLeft' && !checked) {
			normalization.padLeftToLength = null;
			normalization.padLeftChar = null;
		}

		this.cleanupNormalization(row);
		this.previewRequested.emit(row);
	}

	setNormalizationTruncateSuffix(row: MappingGridRow, value: string | null | undefined): void {
		if (!this.isNormalizationMaxLengthEnabled(row)) {
			return;
		}

		const normalization = this.ensureNormalization(row);
		normalization.truncateSuffix = value ?? '';
		this.cleanupNormalization(row);
		this.previewRequested.emit(row);
	}

	setNormalizationMaxLength(row: MappingGridRow, value: string | number | null | undefined): void {
		if (!this.isNormalizationMaxLengthEnabled(row)) {
			return;
		}

		const normalization = this.ensureNormalization(row);
		const numericValue = Number(value);
		normalization.maxLength = Number.isInteger(numericValue) && numericValue > 0
			? numericValue
			: null;
		normalization.truncateToMaxLength = normalization.maxLength ? true : normalization.truncateToMaxLength ?? null;
		this.cleanupNormalization(row);
		this.previewRequested.emit(row);
	}

	setNormalizationPadLeftToLength(row: MappingGridRow, value: string | number | null | undefined): void {
		if (!this.isNormalizationPadLeftEnabled(row)) {
			return;
		}

		const normalization = this.ensureNormalization(row);
		const numericValue = Number(value);
		normalization.padLeftToLength = Number.isInteger(numericValue) && numericValue > 0
			? numericValue
			: null;
		this.cleanupNormalization(row);
		this.previewRequested.emit(row);
	}

	setNormalizationPadLeftChar(row: MappingGridRow, value: string | null | undefined): void {
		if (!this.isNormalizationPadLeftEnabled(row)) {
			return;
		}

		const normalization = this.ensureNormalization(row);
		normalization.padLeftChar = value?.charAt(0) || ' ';
		this.cleanupNormalization(row);
		this.previewRequested.emit(row);
	}

	setNormalizationNewlineMode(row: MappingGridRow, value: string | null | undefined): void {
		const normalization = this.ensureNormalization(row);
		normalization.newlineMode = value && value !== 'Preserve' ? value : null;
		this.cleanupNormalization(row);
		this.previewRequested.emit(row);
	}

	setNormalizationNumberFormat(row: MappingGridRow, value: string | null | undefined): void {
		const normalization = this.ensureNormalization(row);
		normalization.numberFormat = value?.trim() || null;
		this.cleanupNormalization(row);
		this.previewRequested.emit(row);
	}

	setNormalizationDateFormat(row: MappingGridRow, value: string | null | undefined): void {
		const normalization = this.ensureNormalization(row);
		normalization.dateFormat = value?.trim() || null;
		this.cleanupNormalization(row);
		this.previewRequested.emit(row);
	}

	private ensureNormalization(row: MappingGridRow): MappingRuleNormalization {
		if (!row.normalization) {
			row.normalization = {};
		}

		return row.normalization;
	}

	private cleanupNormalization(row: MappingGridRow): void {
		const normalization = row.normalization;
		if (!normalization) {
			return;
		}

		const hasValue = normalization.trimWhitespace === true ||
			normalization.emptyStringToNull === true ||
			normalization.truncateToMaxLength === true ||
			normalization.padLeft === true ||
			!!normalization.maxLength ||
			!!normalization.truncateSuffix ||
			!!normalization.padLeftToLength ||
			!!normalization.padLeftChar ||
			!!normalization.newlineMode ||
			!!normalization.numberFormat ||
			!!normalization.dateFormat;

		if (!hasValue) {
			row.normalization = null;
		}
	}

	private getActiveNormalizationCount(row: MappingGridRow): number {
		const normalization = row.normalization;
		if (!normalization) {
			return 0;
		}

		return [
			normalization.trimWhitespace === true,
			normalization.emptyStringToNull === true,
			normalization.truncateToMaxLength === true || !!normalization.maxLength,
			!!normalization.truncateSuffix,
			normalization.padLeft === true || !!normalization.padLeftToLength,
			!!normalization.newlineMode,
			!!normalization.numberFormat,
			!!normalization.dateFormat
		].filter(Boolean).length;
	}

  getTargetPicklistLabel(row: MappingGridRow, value: any): string | null {
    if (!row.isPicklist || value == null) {
      return null;
    }

    const raw = String(value).trim();
    if (!raw) {
      return null;
    }

    const normalizedRaw = this.normalizeToken(raw);
    const options = row.targetPicklistOptions ?? [];

    const match = options.find(option => {
      const tokens = this.getPicklistTokens(option, row.targetPicklistValueMode);
      return tokens.has(raw) || tokens.has(normalizedRaw);
    });

    return match?.label ?? null;
  }

  getPreviewDisplayValue(value: any): string {
    if (value == null || value === '') {
      return '—';
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return '[object]';
      }
    }

    return String(value);
  }

	getPreviewVisibleValue(value: any): string {
		if (typeof value !== 'string') {
			return this.getPreviewDisplayValue(value);
		}

		if (!this.hasEdgeWhitespace(value)) {
			return value;
		}

		return value
			.replace(/^ +/, match => '·'.repeat(match.length))
			.replace(/ +$/, match => '·'.repeat(match.length));
	}

	getPreviewWhitespaceSummary(value: any): string | null {
		if (typeof value !== 'string' || !this.hasEdgeWhitespace(value)) {
			return null;
		}

		const leadingSpaces = value.match(/^ +/)?.[0].length ?? 0;
		const trailingSpaces = value.match(/ +$/)?.[0].length ?? 0;
		const parts: string[] = [];

		if (leadingSpaces > 0) {
			parts.push(`${leadingSpaces} leading space${leadingSpaces === 1 ? '' : 's'}`);
		}

		if (trailingSpaces > 0) {
			parts.push(`${trailingSpaces} trailing space${trailingSpaces === 1 ? '' : 's'}`);
		}

		return parts.join(', ');
	}

	private hasEdgeWhitespace(value: string): boolean {
		return /^ | $/.test(value);
	}

  getRowStatusLabel(row: MappingGridRow): string {
    const state = this.getRowState(row);

    switch (state) {
      case 'ready':
        return 'Ready';
      case 'issue':
        return 'Needs attention';
      case 'ignored':
        return 'Ignored';
      case 'readonly':
        return 'Read-only';
      case 'pending':
        return this.isMappedRow(row)
          ? 'Mapped'
          : (row.validationMessage || 'Needs input');
      default:
        return 'Needs input';
    }
  }

  private isMappedRow(row: MappingGridRow): boolean {
	if (!this.canEditMapping(row) || row.mode === 'Ignore') {
      return false;
    }

    switch (row.mode) {
      case 'SourceField':
        return !!row.sourceFieldPath;
      case 'Constant':
      case 'Expression':
        return !!row.value?.trim();
      case 'Lookup':
        return !!row.lookup?.returnFieldPath && !!row.lookup?.criteria?.length;
      default:
        return false;
    }
  }

  getSourceFieldHint(row: MappingGridRow): string {
    const selected = this.getSelectedSourceField(row);

    if (!selected) {
      return row.isPicklist
        ? 'Picklist targets work best with a source picklist, but string, number, and boolean values are also allowed.'
        : 'Choose the source field to copy directly into this target field.';
    }

    if (row.isPicklist && selected.isPicklist) {
      return `Selected source is also a picklist${selected.picklistValueMode ? ` (${selected.picklistValueMode})` : ''}.`;
    }

    if (row.isPicklist && !selected.isPicklist) {
      return `Selected source type is ${selected.dataType}. The backend will still try generic picklist matching.`;
    }

    return `Selected source type: ${selected.dataType}.`;
  }

  getRequirementLabel(row: MappingGridRow): string | null {
    if (row.requiredOnCreate && row.requiredOnUpdate) {
      return 'required';
    }

    if (row.requiredOnCreate) {
      return 'required on create';
    }

    if (row.requiredOnUpdate) {
      return 'required on update';
    }

    return null;
  }

	getMetadataSummary(row: MappingGridRow): string {
		const parts: string[] = [];

    const capabilities = [
      row.canRead ? 'read' : null,
      row.canCreate ? 'create' : null,
      row.canUpdate ? 'update' : null
    ].filter(Boolean) as string[];

    if (capabilities.length) {
      parts.push(capabilities.join(' / '));
    }

    if (row.isKey) {
      parts.push('key');
    }

    if (row.isUniqueKey) {
      parts.push('unique key');
    }

    if (row.isReference && row.referenceEntityName) {
      parts.push(`related: ${row.referenceEntityName}`);
    }

    const constraints: string[] = [];

    if (row.format) {
      constraints.push(`format: ${row.format}`);
    }

    if (row.minLength != null && row.maxLength != null) {
      constraints.push(`length: ${row.minLength}-${row.maxLength}`);
    } else if (row.maxLength != null) {
      constraints.push(`max length: ${row.maxLength}`);
    } else if (row.minLength != null) {
      constraints.push(`min length: ${row.minLength}`);
    }

    if (constraints.length) {
      parts.push(constraints.join(', '));
    }

    if (row.isPicklist && (row.targetPicklistOptions?.length ?? 0) > 0) {
      parts.push(`${row.targetPicklistOptions!.length} options`);
    }

		return parts.join(' • ');
	}

	getFieldTitle(row: MappingGridRow): string {
		return row.targetDisplayName || row.targetPath || '';
	}

	getFieldPathContext(row: MappingGridRow): string | null {
		const path = row.targetPath?.trim();
		if (!path || (!path.includes('.') && !path.includes('[]'))) {
			return null;
		}

		return path;
	}

	getFieldInfoTitle(row: MappingGridRow): string {
		const status = this.getRowStatusLabel(row);
		const pathContext = this.getFieldPathContext(row);
		const title = this.getFieldTitle(row);
		return pathContext ? `${title} (${pathContext}) • ${status}` : `${title} • ${status}`;
	}

  getFieldInfoDescription(row: MappingGridRow): string {
    if (row.mode === 'Lookup') {
      const criterionCount = row.lookup?.criteria?.length ?? 0;

      if (!criterionCount) {
        return 'Add one or more match rules to find the related record.';
      }

      return criterionCount === 1
        ? 'This field uses 1 lookup condition to find the related record.'
        : `This field uses ${criterionCount} lookup conditions to find the related record.`;
    }

    if (row.validationMessage) {
      return row.validationMessage;
    }

    if (row.description?.trim()) {
      return row.description.trim();
    }

    return this.getModeDescription(row);
  }

  getFieldInfoSourceSummary(row: MappingGridRow): string {
    if (row.mode === 'Lookup') {
      const referenceEntity =
        row.lookup?.referenceEntityName ||
        row.referenceEntityName ||
        row.reference?.targetEntity ||
        'related entity';

      const returnField = row.lookup?.returnFieldPath;
      if (returnField) {
        return `${referenceEntity} -> ${returnField}`;
      }

      return `Find record in ${referenceEntity}`;
    }

    return row.sourceFieldPath || 'Not selected';
  }

  getFieldInfoLinkKeyLabel(row: MappingGridRow): string {
	if (row.isLinkKey && row.matchOnlyLinkKey) {
	  return 'Match only: finds the target record but is never written to it.';
	}

    return row.isLinkKey ? 'Used to match existing target records.' : 'Not used for record matching.';
  }

  formatFieldInfoPreviewValue(value: any): string {
    return this.getPreviewDisplayValue(value) || 'No preview yet';
  }

  getSourceFieldSearchValue(row: MappingGridRow): string {
    return this.sourceFieldSearchByRowId[row.targetFieldId] ?? '';
  }

  setSourceFieldSearchValue(row: MappingGridRow, value: string | null | undefined): void {
    this.sourceFieldSearchByRowId[row.targetFieldId] = value ?? '';
    this.rebuildSourceTreeForRow(row);
  }

  getSelectedSourceFieldDisplay(row: MappingGridRow): string {
    return row.sourceFieldPath || '';
  }

  getCachedSourceTreeItems(row: MappingGridRow): TreeNode[] {
    const cached = this.sourceTreeItemsByRowId[row.targetFieldId];
    if (cached) {
      return cached;
    }

    this.rebuildSourceTreeForRow(row);
    return this.sourceTreeItemsByRowId[row.targetFieldId] ?? [];
  }

  onSourceTreeItemClick(e: any, row: MappingGridRow): void {
	const item = e?.itemData as TreeNode | undefined;
	if (item?.childrenDeferred) {
		this.deferredSourceNodeExpanded.emit(item);
		return;
	}

	if (!item || item.items?.length) {
		return;
	}

	const originalEvent = e?.event as Event | undefined;
	originalEvent?.stopPropagation();
	if (item.disabled || !item.isMappable) {
		return;
	}

	this.applySourceFieldPath(row, item.path, 'sourceTree.itemClick', true);
}

	onSourceTreeItemExpanded(event: any): void {
		const item = event?.itemData as TreeNode | undefined;
		if (item?.childrenDeferred && item.deferredLoadState !== 'loading') {
			this.deferredSourceNodeExpanded.emit(item);
		}
	}

	retryDeferredSourceNode(event: Event, item: TreeNode): void {
		event.stopPropagation();
		this.deferredSourceNodeExpanded.emit(item);
	}

	private applySourceFieldPathFromValue(row: MappingGridRow, sourceFieldPath: string, reason: string, closeDropdown: boolean): void {
		const sourceField = this.getSourceFieldOptionsForRow(row).find(option => option.path === sourceFieldPath.trim());
		if (!sourceField) {
			return;
		}

		this.applySourceFieldPath(row, sourceField.path, reason, closeDropdown);
	}

	private applySourceFieldPath(row: MappingGridRow, sourceFieldPath: string | null, reason: string, closeDropdown: boolean): void {
		this.captureScrollAnchor(row);
		row.sourceFieldPath = sourceFieldPath;
		this.rebuildSourceTreeForRow(row);

		if (closeDropdown) {
			this.closeSourceDropdown(row);
		}

		this.previewRequested.emit(row);
		this.scheduleScrollAnchorRestore(reason);
	}

	private captureScrollAnchor(row: MappingGridRow): void {
		const marker = this.findMapperRowMarker(row.targetFieldId);
		const rowElement = marker?.closest('tr.dx-data-row') as HTMLElement | null;
		if (!rowElement) {
			return;
		}

		this.pendingScrollAnchor = {
			rowId: row.targetFieldId,
			rowTop: rowElement.getBoundingClientRect().top
		};

		if (this.pendingScrollAnchorTimeout) {
			clearTimeout(this.pendingScrollAnchorTimeout);
		}

		this.pendingScrollAnchorTimeout = setTimeout(() => {
			this.pendingScrollAnchor = null;
			this.pendingScrollAnchorTimeout = null;
		}, 10000);
	}

	private clearPendingScrollAnchor(): void {
		this.pendingScrollAnchor = null;
		if (this.pendingScrollAnchorTimeout) {
			clearTimeout(this.pendingScrollAnchorTimeout);
			this.pendingScrollAnchorTimeout = null;
		}
	}

	private scheduleScrollAnchorRestore(reason: string): void {
		if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
			window.requestAnimationFrame(() => {
				this.restorePendingScrollAnchor(`${reason}:animationFrame`);
				this.clearPendingScrollAnchor();
			});
			return;
		}

		setTimeout(() => {
			this.restorePendingScrollAnchor(`${reason}:timeout`);
			this.clearPendingScrollAnchor();
		}, 0);
	}

	private restorePendingScrollAnchor(reason: string): void {
		const anchor = this.pendingScrollAnchor;
		if (!anchor) {
			return;
		}

		const marker = this.findMapperRowMarker(anchor.rowId);
		const rowElement = marker?.closest('tr.dx-data-row') as HTMLElement | null;
		const scrollContainer = marker ? this.findMapperGridScrollContainer(marker) : null;
		if (!rowElement || !scrollContainer) {
			return;
		}

		const currentTop = rowElement.getBoundingClientRect().top;
		const delta = Math.round((currentTop - anchor.rowTop) * 100) / 100;
		if (Math.abs(delta) < 1) {
			return;
		}

		scrollContainer.scrollTop += delta;
	}

	private findMapperGridScrollContainer(element: HTMLElement): HTMLElement | null {
		const scrollableContainer = element.closest('.dx-scrollable-container') as HTMLElement | null;
		if (scrollableContainer) {
			return scrollableContainer;
		}

		return element.closest('.mapper-grid-scroll-shell') as HTMLElement | null;
	}

	private findMapperRowMarker(targetFieldId: string): HTMLElement | null {
		if (typeof document === 'undefined') {
			return null;
		}

		const markers = Array.from(document.querySelectorAll('[data-mapper-row-id]')) as HTMLElement[];
		return markers.find(x => x.getAttribute('data-mapper-row-id') === targetFieldId) ?? null;
	}

  private resetRowCaches(): void {
		this.clearSourceDropdownPrewarmTimeouts();
    this.sourceTreeItemsByRowId = {};
    this.sourceFieldOptionsByRowId = {};
		this.constantPicklistOptionsByRowId = {};
  }

  private rebuildSourceTreeForRow(row: MappingGridRow): void {
    const query = (this.getSourceFieldSearchValue(row) || '').trim().toLowerCase();
		if (!query && !row.isPicklist && !row.sourceFieldOptions?.length) {
			this.sourceTreeItemsByRowId[row.targetFieldId] = this.sourceTreeItems;
			return;
		}

    const allowedPaths = new Set(this.getSourceFieldOptionsForRow(row).map(x => x.path));
    const selectedPath = row.sourceFieldPath ?? null;

    const mapNode = (node: TreeNode): TreeNode | null => {
      const originalChildren = node.items ?? [];

      const mappedChildren = originalChildren
        .map(child => mapNode(child))
        .filter((x): x is TreeNode => !!x);

      const hadChildrenOriginally = originalChildren.length > 0;
      const hasVisibleChildren = mappedChildren.length > 0;
		const isLeaf = !hadChildrenOriginally && !node.childrenDeferred;
      const isAllowedLeaf = !!node.isMappable && allowedPaths.has(node.path);
      const isSelectedLeaf = !!selectedPath && node.path === selectedPath;

      const haystack = `${node.displayName ?? ''} ${node.path ?? ''} ${node.dataType ?? ''}`.toLowerCase();
      const matchesSearch = !query || haystack.includes(query);

      const includeNode =
        matchesSearch ||
        hasVisibleChildren ||
        (!query && isAllowedLeaf) ||
        isSelectedLeaf;

      if (!includeNode) {
        return null;
      }

      return {
        ...node,
        items: mappedChildren,
			expanded: node.expanded === true || (!!query && hasVisibleChildren),
        disabled: isLeaf ? !isAllowedLeaf : false
      };
    };

    this.sourceTreeItemsByRowId[row.targetFieldId] = (this.sourceTreeItems ?? [])
      .map(node => mapNode(node))
      .filter((x): x is TreeNode => !!x);
  }

	private clearSourceDropdownPrewarmTimeouts(): void {
		Object.values(this.sourceDropdownPrewarmTimeoutByRowId).forEach(timeout => clearTimeout(timeout));
		this.sourceDropdownPrewarmTimeoutByRowId = {};
	}

  private isSourceFieldCompatible(row: MappingGridRow, option: FieldOption): boolean {
    if (!row.isPicklist) {
      return true;
    }

    if (option.isPicklist) {
      return true;
    }

		return ['string', 'integer', 'decimal', 'boolean'].includes(option.dataType.toLowerCase());
  }

  private getSourceFieldScore(row: MappingGridRow, option: FieldOption): number {
    if (!row.isPicklist) {
      if (option.dataType === row.targetDataType) {
        return 0;
      }

      return 10;
    }

    if (option.isPicklist) {
      return 0;
    }

    if (option.dataType === 'String') {
      return 1;
    }

    if (option.dataType === 'Integer' || option.dataType === 'Decimal') {
      return 2;
    }

    if (option.dataType === 'Boolean') {
      return 3;
    }

    return 10;
  }

  private getPicklistTokens(
    option: MappingPicklistOption,
    mode: string | null | undefined
  ): Set<string> {
    const result = new Set<string>();
    const effectiveMode = (mode ?? 'either').toLowerCase();

    if (effectiveMode === 'value' || effectiveMode === 'either') {
      this.addToken(result, option.value);
    }

    if (effectiveMode === 'label' || effectiveMode === 'either') {
      this.addToken(result, option.label);
    }

    this.addToken(result, option.canonicalKey ?? null);

    for (const alias of option.aliases ?? []) {
      this.addToken(result, alias);
    }

    return result;
  }

  private addToken(set: Set<string>, value: string | null | undefined): void {
    if (!value?.trim()) {
      return;
    }

    set.add(value);
    set.add(this.normalizeToken(value));
  }

  private normalizeToken(value: string): string {
    return value.trim().toLowerCase().replace(/[\s\-_]+/g, ' ');
  }
}
